import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isDemoBuild } from './siteConfig.js';
import { AI_MANAGEMENT_PILLARS, MANAGEMENT_COMPARISON } from './hoaProductCatalog.js';

const FEATURES = [
  {
    title: 'Nanny AI management',
    description: '24/7 AI community manager — answers CC&R and billing questions, navigates the portal, assists admins.',
    icon: '🤖',
  },
  {
    title: 'Owner dashboard',
    description: 'Weather, local news, events, and mail delivery status on one home screen.',
    icon: '🏠',
  },
  {
    title: 'HOA billing',
    description: 'Invoices, Stripe payments, and payment history tied to each lot.',
    icon: '💳',
  },
  {
    title: 'Gate access',
    description: 'License plate registration, remote open, and audit logs for secure entry.',
    icon: '🚗',
  },
  {
    title: 'Mailbox intelligence',
    description: 'Ring-powered delivery detection with daily history for absentee owners.',
    icon: '📬',
  },
  {
    title: 'Community engagement',
    description: 'Message board, polls, calendar, documents, and emergency broadcasts.',
    icon: '💬',
  },
  {
    title: 'Mountain monitoring',
    description: 'Well levels, seismic activity, road conditions, river gauges, and flight radar.',
    icon: '⛰️',
  },
  {
    title: 'Weather alert notifications',
    description: 'NWS watches and warnings in a dismissible banner — optional push on mobile, never blocks the portal.',
    icon: '⛈️',
  },
  {
    title: 'Identification tools',
    description: 'AI-assisted plant, wildlife, and mineral galleries for educational discovery.',
    icon: '🔬',
  },
  {
    title: 'Admin portal',
    description: 'Unified hub for users, invoices, polls, mailbox setup, and vehicle logging.',
    icon: '⚙️',
  },
  {
    title: 'Smart-home integrations',
    description: 'Optional add-ons — clubhouse locks, pool pumps, HVAC, lighting, and site sensors tied into one portal.',
    icon: '🏠',
  },
];

const STEPS = [
  { step: '1', title: 'Tour the demo', text: 'See how a self-managed board runs billing, gate access, and owner comms without a PMC.' },
  { step: '2', title: 'Meet Nanny AI', text: 'Ask the assistant about rules, navigation, and community features — like a management desk that never closes.' },
  { step: '3', title: 'Scope your savings', text: 'Use the configurator to compare platform cost vs. a management company and get a quote.' },
];

function MarketingLandingPage({ onTryDemo, onOwnerLogin, isMobile }) {
  const navigate = useNavigate();

  const handleTryDemo = () => {
    if (onTryDemo) {
      onTryDemo();
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className={`marketing-page ${isMobile ? 'marketing-page-mobile' : ''}`}>
      <section className="marketing-hero">
        <div className="marketing-hero-glow" aria-hidden="true" />
        <div className="marketing-hero-inner">
          <p className="marketing-eyebrow">AI HOA management · no management company required</p>
          <h1 className="marketing-headline">
            {isDemoBuild
              ? 'Self-manage your HOA with AI — not a management company'
              : 'AI-powered HOA management for mountain communities'}
          </h1>
          <p className="marketing-lead">
            Stop paying $8–$15 per lot to a management company for work your board can run in-house.
            Nanny AI answers owners 24/7. Billing, gate access, mailbox monitoring, violations, polls,
            and admin tools ship in one portal — white-label ready for your community.
          </p>
          <div className="marketing-cta-row">
            <button type="button" className="marketing-btn marketing-btn-primary" onClick={handleTryDemo}>
              Try live demo
            </button>
            {onOwnerLogin && (
              <button type="button" className="marketing-btn marketing-btn-secondary" onClick={onOwnerLogin}>
                Owner sign in
              </button>
            )}
            <Link to="/for-communities" className="marketing-btn marketing-btn-secondary">
              AI management & pricing
            </Link>
            <Link to="/for-communities/one-pager" className="marketing-btn marketing-btn-ghost">
              Board one-pager
            </Link>
            <Link to="/neighborhood-map" className="marketing-btn marketing-btn-ghost">
              View sample map
            </Link>
          </div>
          <p className="marketing-demo-hint">
            Demo login: <code>owner@pineridge.demo</code> / any password · Admin: <code>admin@pineridge.demo</code>
          </p>
        </div>
      </section>

      <section className="marketing-stats">
        <article className="marketing-stat">
          <strong>Nanny AI</strong>
          <span>Always-on community manager</span>
        </article>
        <article className="marketing-stat">
          <strong>~70%</strong>
          <span>Potential savings vs. typical PMC</span>
        </article>
        <article className="marketing-stat">
          <strong>30+</strong>
          <span>Owner & board features</span>
        </article>
        <article className="marketing-stat">
          <strong>White label</strong>
          <span>Your brand & domain</span>
        </article>
      </section>

      <section className="marketing-section marketing-section-alt marketing-ai-section">
        <h2>Run your HOA without hiring a management company</h2>
        <p className="marketing-section-lead">
          Volunteer boards and self-managed communities don&apos;t need a full-service PMC for daily
          operations. Pair Nanny AI with a complete owner portal — keep legal and accounting advisors
          you trust; replace the expensive management layer.
        </p>
        <div className="marketing-feature-grid">
          {AI_MANAGEMENT_PILLARS.map((pillar) => (
            <article key={pillar.id} className="marketing-feature-card">
              <span className="marketing-feature-icon" aria-hidden="true">{pillar.icon}</span>
              <h3>{pillar.title}</h3>
              <p>{pillar.description}</p>
            </article>
          ))}
        </div>
        <div className="marketing-mgmt-compare">
          {MANAGEMENT_COMPARISON.map((row) => (
            <article
              key={row.approach}
              className={`marketing-mgmt-card ${row.highlight ? 'marketing-mgmt-card-highlight' : ''}`}
            >
              <h3>{row.approach}</h3>
              <p className="marketing-mgmt-cost">
                {row.exampleLots} lots · <strong>${row.exampleMonthly.toLocaleString()}/mo</strong>
                <span className="marketing-mgmt-label"> ({row.monthlyLabel})</span>
              </p>
              <p>{row.notes}</p>
            </article>
          ))}
        </div>
        <p className="marketing-section-lead marketing-ai-cta">
          <Link to="/for-communities#pmc-checklist">PMC replacement checklist</Link>
          {' · '}
          <Link to="/for-communities/one-pager">Printable board one-pager</Link>
          {' · '}
          <Link to="/for-communities">Plans & configurator</Link>
        </p>
      </section>

      <section className="marketing-section">
        <h2>Everything owners expect — in one place</h2>
        <p className="marketing-section-lead">
          Replace scattered emails, PDFs, and “call the management company” with a polished portal your
          board administers and Nanny AI helps support.
        </p>
        <div className="marketing-feature-grid">
          {FEATURES.map((feature) => (
            <article key={feature.title} className="marketing-feature-card">
              <span className="marketing-feature-icon" aria-hidden="true">{feature.icon}</span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="marketing-section marketing-section-alt">
        <h2>How sales teams use this demo</h2>
        <div className="marketing-steps">
          {STEPS.map((item) => (
            <article key={item.step} className="marketing-step-card">
              <span className="marketing-step-num">{item.step}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="marketing-section">
        <h2>Built for real operations</h2>
        <ul className="marketing-checklist">
          <li>Stripe invoice payments with saved cards</li>
          <li>Ring mailbox integration with delivery history</li>
          <li>License plate gate access with audit trail</li>
          <li>Vehicle sighting log for admin security review</li>
          <li>Email notifications for emergencies and board updates</li>
          <li>Firestore security rules and admin custom claims</li>
        </ul>
      </section>

      <section className="marketing-cta-band">
        <h2>See AI HOA management in action</h2>
        <p>No signup required — tour the demo, talk to Nanny, and compare costs vs. a management company.</p>
        <button type="button" className="marketing-btn marketing-btn-primary marketing-btn-lg" onClick={handleTryDemo}>
          Launch demo portal
        </button>
        <Link to="/for-communities" className="marketing-btn marketing-btn-secondary marketing-btn-lg" style={{ marginLeft: '0.5rem' }}>
          Build your portal
        </Link>
        <p className="marketing-legal-links">
          <Link to="/privacy">Privacy Policy</Link>
          {' · '}
          <Link to="/terms">Terms and Conditions</Link>
        </p>
      </section>
    </div>
  );
}

export default MarketingLandingPage;